import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { TooltipProvider } from '../ui/tooltip';
import { useToast } from '../../hooks/use-toast';
import {
    addImplantsToFolder,
    removeImplantsFromFolder,
    ImplantMember,
} from '../../services/api/folderService';
import { SelectedImplantsList } from './SelectedImplantsList';
import {
    ImplantCatalogPicker,
    ImplantBrief,
} from './ImplantCatalogPicker';

interface ManageImplantsDialogProps {
    folderId: string;
    existingImplants: ImplantMember[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onChanged: () => void;
}

export const ManageImplantsDialog = ({
    folderId,
    existingImplants,
    open,
    onOpenChange,
    onChanged,
}: ManageImplantsDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [selectedItems, setSelectedItems] = useState<Map<string, ImplantBrief>>(
        new Map()
    );
    const [submitting, setSubmitting] = useState(false);
    const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
    
    useEffect(() => {
        if (!open) {
            setSelectedItems(new Map());
            setPendingRemoveId(null);
        }
    }, [open]);

    const existingSet = useMemo(
        () => new Set(existingImplants.map((i) => i._id)),
        [existingImplants]
    );

    const toggleItem = (implant: ImplantBrief) => {
        setSelectedItems((prev) => {
            const next = new Map(prev);
            if (next.has(implant._id)) next.delete(implant._id);
            else next.set(implant._id, implant);
            return next;
        });
    };

    const selectMany = (items: ImplantBrief[], select: boolean) => {
        setSelectedItems((prev) => {
            const next = new Map(prev);
            for (const item of items) {
                if (select) next.set(item._id, item);
                else next.delete(item._id);
            }
            return next;
        });
    };

    const undoPending = (id: string) => {
        setSelectedItems((prev) => {
            const next = new Map(prev);
            next.delete(id);
            return next;
        });
    };

    const handleRemove = async (implantId: string) => {
        setPendingRemoveId(implantId);
        try {
            await removeImplantsFromFolder(folderId, [implantId]);
            onChanged();
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.manageImplants.toasts.removeFailed'),
                variant: 'destructive',
            });
        } finally {
            setPendingRemoveId(null);
        }
    };

    const handleSubmit = async () => {
        if (selectedItems.size === 0) return;
        setSubmitting(true);
        try {
            await addImplantsToFolder(
                folderId,
                Array.from(selectedItems.keys())
            );
            const count = selectedItems.size;
            toast({
                title: t('folders.manageImplants.addedToast', { count }),
            });
            onChanged();
            onOpenChange(false);
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.manageImplants.toasts.addFailed'),
                variant: 'destructive',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const selectedCount = selectedItems.size;
    const pendingImplants = Array.from(selectedItems.values());

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[85vh] max-w-4xl">
                <DialogHeader>
                    <DialogTitle>
                        {t('folders.manageImplants.title')}
                    </DialogTitle>
                </DialogHeader>

                <TooltipProvider delayDuration={400}>
                    <div className="flex min-w-0 flex-col gap-4 md:h-[60vh] md:flex-row">
                        <SelectedImplantsList
                            existingImplants={existingImplants}
                            pendingImplants={pendingImplants}
                            pendingRemoveId={pendingRemoveId}
                            onRemove={handleRemove}
                            onUndoPending={undoPending}
                        />

                        <div
                            aria-hidden="true"
                            className="hidden w-px bg-subtle md:block"
                        />

                        <ImplantCatalogPicker
                            open={open}
                            existingSet={existingSet}
                            selectedItems={selectedItems}
                            onToggleItem={toggleItem}
                            onSelectMany={selectMany}
                        />
                    </div>
                </TooltipProvider>

                <DialogFooter className="items-center justify-between sm:justify-between">
                    <span className="text-xs text-subtle-foreground">
                        {selectedCount > 0 &&
                            t('folders.manageImplants.selected', {
                                count: selectedCount,
                            })}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            variant={
                                selectedCount > 0 ? 'outline' : 'default'
                            }
                            onClick={() => onOpenChange(false)}
                            disabled={submitting}
                        >
                            {selectedCount > 0
                                ? t('folders.manageImplants.cancel')
                                : t('folders.manageImplants.done')}
                        </Button>
                        {selectedCount > 0 && (
                            <Button
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting
                                    ? t('folders.manageImplants.adding')
                                    : t('folders.manageImplants.add', {
                                          count: selectedCount,
                                      })}
                            </Button>
                        )}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
